import { clsx } from "clsx";
import { CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { timeAgo } from "@/lib/format";
import { LoadingGrid, StateView } from "@/components/ui/StateView";
import { useBookingPopupStore } from "@/store/booking-popup-store";
import { useMarkNotificationRead, useVisibleNotifications, type AppNotification } from "./useNotifications";
import { NotificationSoundPicker } from "./NotificationSoundPicker";

export function NotificationsPage() {
  const navigate = useNavigate();
  const notifications = useVisibleNotifications();
  const markNotificationRead = useMarkNotificationRead();
  const reopenBookingPopup = useBookingPopupStore((state) => state.reopen);
  const visibleNotifications = notifications.data ?? [];
  const unread = visibleNotifications.filter((notification) => !notification.isRead);

  const openNotification = (notification: AppNotification) => {
    if (!notification.isRead) markNotificationRead.mutate(notification.id);
    if (notification.bookingId) {
      if (notification.type === "NEW_BOOKING_REQUEST" && notifications.openBookingIds.has(notification.bookingId)) reopenBookingPopup(notification.bookingId);
      navigate("/bookings");
    }
  };

  if (notifications.isLoading) return <main className="page notifications-page"><NotificationSoundPicker /><LoadingGrid /></main>;
  if (notifications.isError) return <main className="page notifications-page"><NotificationSoundPicker /><StateView title="Couldn't load notifications" message="Check the backend connection and try once more." action={() => void notifications.refetch()} /></main>;

  return (
    <main className="page notifications-page">
      <NotificationSoundPicker />
      <div className="notification-card">
        <div className="notification-card__head">
          <h2>Notifications</h2>
          {unread.length > 0 && (
            <button type="button" className="notification-panel__mark-all" onClick={() => unread.forEach((notification) => markNotificationRead.mutate(notification.id))}>
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>

        {!visibleNotifications.length ? (
          <div className="notification-panel__empty">You're all caught up.</div>
        ) : (
          <div className="notification-list">
            {visibleNotifications.map((notification) => (
              <button type="button" key={notification.id} className={clsx("notification-item", !notification.isRead && "notification-item--unread")} onClick={() => openNotification(notification)}>
                <span className="notification-item__dot" />
                <span className="notification-item__body">
                  <strong>{notification.title}</strong>
                  <small>{notification.body}</small>
                  <small className="notification-item__time">{timeAgo(notification.createdAt)}</small>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
